import { useState, useEffect, useCallback } from 'react';
import { notificationsApi } from '@/api/notifications-api';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    const byte = bytes[i];
    if (byte !== undefined) {
      binary += String.fromCharCode(byte);
    }
  }
  return window.btoa(binary);
}

export interface UseWebPushReturn {
  isSupported: boolean;
  permission: NotificationPermission | 'unsupported';
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  subscribeToPush: () => Promise<boolean>;
  unsubscribeFromPush: () => Promise<boolean>;
  checkSubscriptionStatus: () => Promise<void>;
}

export function useWebPush(): UseWebPushReturn {
  const isSupported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    isSupported ? Notification.permission : 'unsupported',
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkSubscriptionStatus = useCallback(async () => {
    if (!isSupported) {
      setPermission('unsupported');
      return;
    }

    try {
      setPermission(Notification.permission);
      const registration = await navigator.serviceWorker.getRegistration('/sw.js');
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } else {
        setIsSubscribed(false);
      }
    } catch (err: any) {
      console.warn('[useWebPush] Erro ao verificar status de inscrição push:', err);
    }
  }, [isSupported]);

  useEffect(() => {
    checkSubscriptionStatus();
  }, [checkSubscriptionStatus]);

  const subscribeToPush = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Web Push não é suportado neste navegador.');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 1. Solicitar permissão de notificação
      const currentPermission = await Notification.requestPermission();
      setPermission(currentPermission);

      if (currentPermission !== 'granted') {
        setError('Permissão de notificações não concedida pelo usuário.');
        setIsLoading(false);
        return false;
      }

      // 2. Registrar Service Worker
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });
      await navigator.serviceWorker.ready;

      // 3. Obter VAPID public key
      let vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      if (!vapidPublicKey) {
        const res = await notificationsApi.getVapidPublicKey();
        vapidPublicKey = res.publicKey;
      }

      if (!vapidPublicKey) {
        throw new Error('Chave pública VAPID não configurada no servidor.');
      }

      const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);

      // 4. Obter ou criar inscrição no PushManager
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedVapidKey as unknown as BufferSource,
        });
      }

      const p256dhBuffer = subscription.getKey('p256dh');
      const authBuffer = subscription.getKey('auth');

      const p256dhKey = arrayBufferToBase64(p256dhBuffer);
      const authKey = arrayBufferToBase64(authBuffer);

      // 5. Enviar ao backend
      await notificationsApi.registerPushSubscription({
        endpoint: subscription.endpoint,
        p256dhKey,
        authKey,
        userAgent: navigator.userAgent,
      });

      setIsSubscribed(true);
      setIsLoading(false);
      return true;
    } catch (err: any) {
      const msg = err instanceof Error ? err.message : 'Falha ao ativar notificações push';
      setError(msg);
      setIsLoading(false);
      return false;
    }
  }, [isSupported]);

  const unsubscribeFromPush = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;

    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.getRegistration('/sw.js');
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await notificationsApi.unregisterPushSubscription(subscription.endpoint).catch(() => null);
          await subscription.unsubscribe();
        }
      }
      setIsSubscribed(false);
      setIsLoading(false);
      return true;
    } catch (err: any) {
      const msg = err instanceof Error ? err.message : 'Falha ao desativar notificações push';
      setError(msg);
      setIsLoading(false);
      return false;
    }
  }, [isSupported]);

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    subscribeToPush,
    unsubscribeFromPush,
    checkSubscriptionStatus,
  };
}
